import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { Radio, Loader2 } from "lucide-react";

const SOURCE_COLORS = {
  wordpress_cf7: "#3B82F6",  // blue — Website form
  whatsapp: "#10B981",       // emerald — WhatsApp
  phone: "#F59E0B",          // amber — Phone call
  walk_in: "#8B5CF6",        // purple — Walk-in
  referral: "#EC4899",       // pink — Referral
  other: "#64748B",          // slate — Other/unknown
};

const SOURCE_LABEL = {
  wordpress_cf7: "Website",
  whatsapp: "WhatsApp",
  phone: "Teléfono",
  walk_in: "Walk-in",
  referral: "Referido",
  other: "Otro",
};

/** Pie chart showing lead distribution by acquisition channel. */
export default function SourceStatsChart({ days = 90 }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/leads/source-stats?days=${days}`);
        setData(r.data);
      } catch {
        setData({ items: [], total: 0 });
      } finally {
        setLoading(false);
      }
    })();
  }, [days]);

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6 flex items-center justify-center min-h-[220px]">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const total = data?.total || 0;
  const chartData = (data?.items || []).map((it) => ({
    name: SOURCE_LABEL[it.source] || it.source,
    key: it.source,
    value: it.count,
    percent: it.percent,
  }));

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="source-stats-chart">
      <div className="flex items-center gap-2 mb-1">
        <div className="p-1.5 rounded-md bg-brand-blue/10 text-brand-blue">
          <Radio className="h-4 w-4" />
        </div>
        <h3 className="font-semibold text-slate-900">{t("source_stats_title")}</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        {t("source_stats_subtitle").replace("{total}", total).replace("{days}", days)}
      </p>

      {total === 0 ? (
        <div className="py-10 text-center text-sm text-slate-400 italic">
          {t("source_stats_empty")}
        </div>
      ) : (
        <div className="h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={80}
                paddingAngle={2}
                label={(entry) => (entry.percent >= 5 ? `${entry.percent}%` : "")}
                labelLine={false}
              >
                {chartData.map((entry) => (
                  <Cell key={entry.key} fill={SOURCE_COLORS[entry.key] || "#64748B"} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name, props) => [
                  `${value} leads (${props.payload.percent}%)`,
                  name,
                ]}
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                  fontSize: 12,
                }}
              />
              <Legend
                verticalAlign="middle"
                align="right"
                layout="vertical"
                iconType="circle"
                wrapperStyle={{ fontSize: 12 }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
